import { chain, get, isEmpty, size, startCase } from 'lodash';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { makeStyles } from 'tss-react/mui';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Button from '../ReusableComponents/Button/Button';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import PurchaseOrderAPI from '../../Lib/Api/purchase-order';
import LoadingText from '../ReusableComponents/LoadingText/LoadingText';
import DetailField from '../ReusableComponents/DetailField/DetailField';
import ReceivingPackingListUpload from './ReceivingPackingListUpload';

const useStyles = makeStyles()(() => ({
  root: {
    minHeight: '500px',
  },
}));

export default function QuickOrderSearchModal(props) {
  const { open, onClose, defaultValue = '', currentUser, perms } = props;
  const { classes } = useStyles();
  const [poNumber, setPONumber] = useState(defaultValue);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);

  const searchPO = () => {
    if (isEmpty(poNumber)) return;

    setSearching(true);
    PurchaseOrderAPI.findOneByPONumber(poNumber, { includes: 'vendor' })
      .then(({ purchaseOrder }) => {
        setOrder(purchaseOrder);
        setSearching(false);
        setError(null);
      })
      .catch((err) => {
        setSearching(false);
        setOrder(null);
        setError(err);
      });
  };

  useEffect(() => {
    searchPO();
  }, [defaultValue]);

  const topItems = chain(order)
    .get('purchase_order_lines')
    .filter((item) => item.quantity > 0)
    .value();

  const topStatuses = chain(topItems)
    .map((item) =>
      item.top_status ? `${startCase(item.top_status)} - ${item.size}` : null,
    )
    .compact()
    .uniq()
    .value();

  const delivery = get(order, 'delivery');
  const booking = get(delivery, 'booking');
  const deliveryLocation = get(booking, 'location.name');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid="quick-order-search-modal"
    >
      <DialogContent classes={{ root: classes.root }}>
        <Grid container alignItems="flex-end">
          <TextInput
            className={classes.input}
            label="PO Number"
            value={poNumber}
            onChange={({ target: { value } }) => {
              setError(null);
              setPONumber(value);
            }}
          />
          <Button
            className={classes.button}
            onClick={searchPO}
            variant="outlined"
            color="primary"
            small
          >
            {searching ? <LoadingText text="Searching" /> : 'Search'}
          </Button>
        </Grid>

        {error && (
          <div className={classes.error}>
            {`Purchase order number ${poNumber} was not found, please make sure you
          have entered the number correctly`}
          </div>
        )}

        {!searching && !error && order && (
          <div>
            <DetailField minWidth="50px" label="Due Date">
              <span>{moment.utc(order.due_date).format('L')}</span>
            </DetailField>
            <DetailField minWidth="50px" label="TOP Due Date">
              <span>{moment.utc(order.top_due_date).format('L')}</span>
            </DetailField>
            <DetailField minWidth="50px" label="Original Due Date">
              <span>{moment.utc(order.original_due_date).format('L')}</span>
            </DetailField>
            <DetailField minWidth="50px" label="Vendor Name">
              <span>{get(order, 'vendor.company_name') || '-'}</span>
            </DetailField>
            <DetailField minWidth="50px" label="Vendor Grade">
              <span>{get(order, 'vendor.grade') || '-'}</span>
            </DetailField>
            <DetailField minWidth="50px" label="Buyer Name">
              <span>{order.buyer_name}</span>
            </DetailField>
            <div className={classes.detail}>
              <DetailField minWidth="50px" label="Top Status">
                {size(topStatuses) ? (
                  <div>
                    {topStatuses.map((status) => (
                      <div key={status}>{status}</div>
                    ))}
                  </div>
                ) : (
                  <div>Not Set</div>
                )}
              </DetailField>
            </div>
            <DetailField label={delivery ? 'Delivery On' : 'Delivery Status'}>
              <span>
                {delivery
                  ? moment.utc(booking.starts_at).format('llll')
                  : 'Not Scheduled'}
              </span>
            </DetailField>
            {delivery && (
              <DetailField label="Delivery Location">
                <span>{startCase(deliveryLocation)}</span>
              </DetailField>
            )}

            <DetailField label="Packing List (Uploaded by Vendor)">
              {get(order, 'packing_list.exists') ? (
                <Button
                  variant="outlined"
                  color="primary"
                  small
                  onClick={() =>
                    window.open(get(order, 'packing_list.read'), '_blank')
                  }
                >
                  Download
                </Button>
              ) : (
                <span>Vendor Uploaded Packing List Missing</span>
              )}
            </DetailField>

            <DetailField label="Packing List (Uploaded by FN)">
              <ReceivingPackingListUpload
                order={order}
                onUpload={searchPO}
                currentUser={currentUser}
                perms={perms}
              />
            </DetailField>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

QuickOrderSearchModal.propTypes = {
  open: PropTypes.bool.isRequired,
  defaultValue: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({}).isRequired,
  perms: PropTypes.shape({
    canUpdatePackingList: PropTypes.bool,
    canReadPackingList: PropTypes.bool,
  }).isRequired,
};
